import React, { createContext, useContext, useEffect, useState } from 'react'
import type { AuthState } from '@okta/okta-auth-js'
import { oktaAuth } from '../../auth/okta'
import { regionsForGroups, allowedCountries } from '../../auth/regions'

interface User {
  username: string
  display_name: string
  email: string
  role: string
  groups: string[]
}

interface AuthContextType {
  isAuthenticated: boolean
  user: User | null
  login: () => Promise<void>
  logout: () => void
  isLoading: boolean
  // admin-* and user-* groups are identical in capability, so any authorized
  // user gets full powers within their region(s). These collapse to "authorized".
  isSuperAdmin: boolean
  isAdmin: boolean
  // Region-segregation: the regions / countries this user may access.
  isAuthorized: boolean
  regions: string[]
  allowedCountries: Set<string>
  token: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: React.ReactNode
}

function buildUser(claims: Record<string, any> | undefined): { user: User | null; groups: string[] } {
  if (!claims) return { user: null, groups: [] }
  const groups: string[] = Array.isArray(claims.groups) ? claims.groups : []
  const user: User = {
    username: claims.preferred_username || claims.email || claims.sub,
    display_name: claims.name || claims.preferred_username || claims.email || 'User',
    email: claims.email || claims.preferred_username || claims.sub || '',
    role: groups.length ? 'admin' : 'none',
    groups,
  }
  return { user, groups }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [groups, setGroups] = useState<string[]>([])
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const handleAuthStateChange = (authState: AuthState | null) => {
      const authed = !!authState?.isAuthenticated
      setIsAuthenticated(authed)
      if (authed) {
        const claims = authState?.idToken?.claims as Record<string, any> | undefined
        const built = buildUser(claims)
        setUser(built.user)
        setGroups(built.groups)
        setToken(oktaAuth.getIdToken() || null)
      } else {
        setUser(null)
        setGroups([])
        setToken(null)
      }
      setIsLoading(false)
    }

    oktaAuth.authStateManager.subscribe(handleAuthStateChange)
    // Kick off the initial auth-state computation (also processes any existing session).
    oktaAuth.start()

    return () => {
      oktaAuth.authStateManager.unsubscribe(handleAuthStateChange)
      oktaAuth.stop()
    }
  }, [])

  const login = async () => {
    await oktaAuth.signInWithRedirect({ originalUri: '/' })
  }

  const logout = () => {
    oktaAuth.signOut({ postLogoutRedirectUri: window.location.origin })
  }

  const regions = regionsForGroups(groups)
  const isAuthorized = regions.length > 0
  // Full powers within region for any authorized user (no per-action gating).
  const isSuperAdmin = isAuthorized
  const isAdmin = isAuthorized

  const value: AuthContextType = {
    isAuthenticated,
    user,
    login,
    logout,
    isLoading,
    isSuperAdmin,
    isAdmin,
    isAuthorized,
    regions,
    allowedCountries: allowedCountries(groups),
    token,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
